import { useEffect, useRef, useState } from 'react';
import type { Filters, LostEvent, Reason } from '../lib/types';
import { ink } from '../lib/theme';
import { EventCard, type EventCardHandlers } from './EventCard';
import { ErrorBanner, Skeleton } from './primitives';

/**
 * Lista de eventos com infinite scroll e os atalhos de teclado do PRD §9-C
 * (↑/↓ movem o foco, A audita, E abre evidência, R abre classificar).
 * O card focado tem indicador visual — sem ele o atalho não faria sentido.
 */
export function EventList({ items, loading, loadingMore, error, hasMore, busyIds, variant, reasons, filters, handlers, onLoadMore, onRetry, empty }: {
  items: LostEvent[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  busyIds: Set<number>;
  variant: 'fila' | 'confirmados';
  reasons: Reason[];
  /** Período ativo — a correlação de eventos relacionados (EventAuditModal) fica presa a ele. */
  filters: Filters;
  handlers: EventCardHandlers;
  onLoadMore: () => void;
  onRetry: () => void;
  empty: React.ReactNode;
}) {
  const [focus, setFocus] = useState<number>(-1);
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const sentinel = useRef<HTMLDivElement>(null);

  // Infinite scroll: carrega a próxima página ao aproximar do fim (PRD §9-A).
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasMore || loading) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) onLoadMore(); },
      { rootMargin: '400px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loading, onLoadMore]);

  useEffect(() => { setFocus(-1); }, [variant, items.length === 0]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Nunca sequestrar teclas enquanto o usuário digita num campo.
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (items.length === 0) return;

      const move = (delta: number) => {
        e.preventDefault();
        setFocus((f) => {
          const next = Math.max(0, Math.min(items.length - 1, (f < 0 ? 0 : f + delta)));
          cardRefs.current.get(items[next]?.id)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          return next;
        });
      };

      if (e.key === 'ArrowDown') return move(1);
      if (e.key === 'ArrowUp') return move(-1);

      const ev = focus >= 0 ? items[focus] : null;
      if (!ev) return;
      const el = cardRefs.current.get(ev.id);

      if (e.key.toLowerCase() === 'a') { e.preventDefault(); handlers.onAudit(ev); }
      if (e.key.toLowerCase() === 'e') {
        e.preventDefault();
        const r = el?.getBoundingClientRect();
        handlers.onOpenEvidence(
          ev, { x: r ? r.right - 90 : window.innerWidth / 2, y: r ? r.top + 40 : window.innerHeight / 2 },
          handlers.onSetCorrection, handlers.onClearCorrection,
        );
      }
      if (e.key.toLowerCase() === 'r') {
        e.preventDefault();
        // "classificar ▾" é o último botão do card — dispará-lo por clique
        // reaproveita o mesmo dropdown do mouse, sem duplicar estado.
        const btns = el?.querySelectorAll('button');
        if (btns && btns.length > 0) btns[btns.length - 1].click();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [items, focus, handlers]);

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {Array.from({ length: 8 }, (_, i) => <Skeleton key={i} height={56} radius={14} />)}
      </div>
    );
  }

  if (error) return <ErrorBanner message={error} onRetry={onRetry} />;
  if (items.length === 0) return <>{empty}</>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {items.map((ev, i) => (
        <EventCard
          key={ev.id} ev={ev} reasons={reasons} filters={filters} variant={variant}
          focused={focus === i} busy={busyIds.has(ev.id)} handlers={handlers}
          cardRef={(el) => { if (el) cardRefs.current.set(ev.id, el); else cardRefs.current.delete(ev.id); }}
        />
      ))}

      <div ref={sentinel} style={{ height: 1 }} />

      {loadingMore && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[0, 1, 2].map((i) => <Skeleton key={i} height={56} radius={14} />)}
        </div>
      )}

      <div style={{ textAlign: 'center', fontSize: 11, color: ink(0.28), padding: '10px 0 6px' }}>
        {hasMore ? 'carregando mais 50 eventos ao chegar no fim' : `${items.length} evento${items.length === 1 ? '' : 's'} · fim da lista`}
      </div>
    </div>
  );
}
